import Raven from 'raven-js';
import { getBundledProductsForSkuId, getIsSmartCrmSku, getProduct, getProductDataIsEmpty, getProductForSkuId, getProductVersion, getQuantityPackForProductVersion, highestProductLevel, productHasBundledProducts, skuIdHasBundledProducts } from './productsUtils';
import { isQuantityPackDetails, isValidProductLevel } from './typeguards';
export const getHasSkuInProductInventory = (state, {
  skuId
}) => {
  const {
    productInventory
  } = state;
  if (!productInventory.products) {
    return false;
  }
  return productInventory.products.some(productInventoryObject => {
    if (productInventoryObject.skuId === skuId) return true;
    return productInventoryObject.quantityPackDetails.some(quantityPack => quantityPack.skuId === skuId);
  });
};
const versionNumberComparisonDefault = (ownedVersion, targetVersion) => ownedVersion === targetVersion;
export const getHasProductVersionInProductInventory = (state, {
  skuId,
  apiName,
  versionNumber,
  versionNumberComparison = versionNumberComparisonDefault
}) => {
  const {
    productInventory
  } = state;
  if (!productInventory.products) {
    return false;
  }
  return productInventory.products.some(({
    skuId: ownedSkuId,
    productApiName: ownedApiName
  }) => {
    if (skuId) {
      return ownedSkuId === skuId;
    } else if (versionNumber !== undefined && apiName !== undefined) {
      const version = getProductVersion(state, {
        skuId: ownedSkuId
      });
      return version && apiName === ownedApiName && versionNumberComparison(version.versionNumber, versionNumber);
    }
    return false;
  });
};
const productHasAttributeFilters = (state, {
  productAttributeFilters = {},
  productId
}) => {
  return Object.keys(productAttributeFilters).every(attributeFilter => {
    const product = getProduct(state, {
      productId
    });
    if (!product) return false;
    return product[attributeFilter === 'productId' ? 'id' : attributeFilter] === productAttributeFilters[attributeFilter];
  });
};
export const getProductVersionsFromProductInventory = (state, {
  productAttributeFilters = {}
} = {}) => {
  const {
    productInventory
  } = state;
  const isEmpty = getProductDataIsEmpty(state);
  if (isEmpty) {
    // Generally this means that we call this util before all the data is fetched.
    // If we see this error investigate what race condition might be causing it and update accordingly
    console.error('Pricing pages lib: Attempted fetch of empty product inventory data');
    Raven.captureException(new Error('Pricing pages lib: Attempted fetch of empty product inventory data'));
    return [];
  }
  return productInventory.products ? productInventory.products.map(({
    skuId
  }) => getProductVersion(state, {
    skuId
  })).filter(productVersion => !!productVersion).filter(({
    productId,
    skuId
  }) => productHasAttributeFilters(state, {
    productAttributeFilters,
    productId
  }) || skuIdHasBundledProducts(state, {
    skuId
  }) && getBundledProductsForSkuId(state, {
    skuId
  }).find(product => product ? productHasAttributeFilters(state, {
    productAttributeFilters,
    productId: product.id
  }) : false)) : [];
};
export const ownedBundleWithProductLine = (state, {
  productLine
}) => {
  const productVersions = getProductVersionsFromProductInventory(state);
  return productVersions.find(productVersion => skuIdHasBundledProducts(state, {
    skuId: productVersion.skuId
  }) && productVersion.bundledProducts.some(({
    productLine: bundledProductLine
  }) => bundledProductLine === productLine));
};
export const getProductVersionFromProductInventory = (state, {
  productAttributeFilters
}) => {
  const ownedProductVersions = getProductVersionsFromProductInventory(state, {
    productAttributeFilters
  });
  if (ownedProductVersions.length === 0) return null;
  if (ownedProductVersions.length > 1) {
    const message = 'Unexpected multiple products found in ownedProductVersionSelector';
    console.error(message, {
      ownedProductVersions,
      productAttributeFilters
    });
  }
  return ownedProductVersions[0];
};
export const getProductsFromProductInventory = (state, {
  productAttributeFilters = {}
} = {}) => {
  const {
    productData: {
      skuMap
    }
  } = state;
  const ownedProductVersions = getProductVersionsFromProductInventory(state, {
    productAttributeFilters
  });
  const ownedProductIds = [];
  ownedProductVersions.forEach(({
    skuId
  }) => {
    const productVersion = skuMap[skuId];
    if (productVersion && !ownedProductIds.includes(productVersion.productId)) {
      ownedProductIds.push(productVersion.productId);
    }
  });
  return ownedProductIds.map(productId => getProduct(state, {
    productId
  })).filter(product => !!product);
};
export const getProductsFromProductInventoryQuantity = (state, {
  skuId,
  calculateIncludedWithQuantityPack = true
}) => {
  const {
    productInventory,
    productData: {
      skuMap
    }
  } = state;
  const productsFromProductInventory = productInventory.products;
  if (!productsFromProductInventory) {
    return 0;
  }
  for (let i = 0; i < productsFromProductInventory.length; i++) {
    if (productsFromProductInventory[i].skuId === skuId) {
      return productsFromProductInventory[i].skuQuantityPurchased;
    }
    const quantityPacks = productsFromProductInventory[i].quantityPackDetails;
    if (quantityPacks) {
      for (let p = 0; p < quantityPacks.length; p++) {
        if (quantityPacks[p].skuId === skuId) {
          const sku = skuMap[quantityPacks[p].skuId];
          const includedWithQuantityPack = isQuantityPackDetails(sku) && sku.includedWithQuantityPack || 0;
          const skuQuantityPurchased = quantityPacks[p].skuQuantityPurchased;
          return calculateIncludedWithQuantityPack ? skuQuantityPurchased * includedWithQuantityPack : skuQuantityPurchased;
        }
      }
    }
  }
  return 0;
};

/**
 * @deprecated Use useProductInventoryQuantityPackQuantity instead
 */
export const getProductsFromProductInventoryQuantityPackQuantity = (state, {
  productIdentifier,
  quantityPackApiName
}) => {
  const {
    productInventory
  } = state;
  const productVersion = getProductVersionFromProductInventory(state, {
    productAttributeFilters: Object.assign({}, productIdentifier)
  });
  if (!productVersion) return 0;
  const quantityPack = getQuantityPackForProductVersion(state, {
    productVersionSkuId: productVersion.skuId,
    quantityPackApiName
  });
  if (!quantityPack) return 0;
  const {
    skuId: quantityPackSkuId,
    includedWithQuantityPack
  } = quantityPack;
  if (!productInventory.products) {
    return 0;
  }

  // Search through all owned quantity packs for the right skuId
  for (const ownedProductVersion of productInventory.products) {
    for (const ownedQuantityPack of ownedProductVersion.quantityPackDetails || []) {
      if (ownedQuantityPack.skuId === quantityPackSkuId) {
        return ownedQuantityPack.skuQuantityPurchased * (includedWithQuantityPack !== null && includedWithQuantityPack !== void 0 ? includedWithQuantityPack : 0);
      }
    }
  }
  return 0;
};

/**
 * With the introduction of standalone core seats, we also need to get the highest product level for core seats
 * from the product inventory, rather than just using the highest product level for hub and bundle products.
 */
export const getHighestProductLevelFromProductInventory = (state, {
  productLine
} = {}) => {
  const ownedProducts = productLine ? getProductsFromProductInventory(state, {
    productAttributeFilters: {
      productLine
    }
  }) : getProductsFromProductInventory(state);
  const filteredOwnedProducts = ownedProducts.filter(product => {
    const skuId = product.currentVersion.skuId;
    return product.productLine && product.productLevel || productHasBundledProducts(state, {
      productId: product.id
    }) || getIsSmartCrmSku(state, {
      skuId
    });
  });
  return highestProductLevel(...filteredOwnedProducts.map(product => product.productLevel).filter(isValidProductLevel));
};
export const getHighestPackagedQuantityForByUnitNameFromProductInventory = (state, {
  unitName
}) => {
  var _productInventory$pro;
  const {
    productInventory
  } = state;
  if (!((_productInventory$pro = productInventory.products) !== null && _productInventory$pro !== void 0 && _productInventory$pro.length)) {
    return 0;
  }
  let maxQuantity = 0;
  for (const product of productInventory.products) {
    var _productVersion$packa;
    const productVersion = getProductVersion(state, {
      skuId: product.skuId
    });
    if (!(productVersion !== null && productVersion !== void 0 && (_productVersion$packa = productVersion.packagedQuantities) !== null && _productVersion$packa !== void 0 && _productVersion$packa.length)) {
      continue;
    }
    const isBundle = productVersion.isBundle;
    const {
      productLine
    } = getProductForSkuId(state, {
      skuId: product.skuId
    });

    // Skip if it is neither a product line nor a bundle
    if (!productLine && !isBundle) {
      continue;
    }
    const packagedQuantity = productVersion.packagedQuantities.find(pq => pq.name === unitName);
    if (packagedQuantity !== null && packagedQuantity !== void 0 && packagedQuantity.quantity && packagedQuantity.quantity > maxQuantity) {
      maxQuantity = packagedQuantity.quantity;
    }
  }
  return maxQuantity;
};